"use client"

import { keyframes } from "@emotion/react"
import styled from "@emotion/styled"
import { useLocale } from "next-intl"
import Image from "next/image"
import { useEffect, useRef, useState } from "react"
import { extractProperty, getLinkHrefFromMultiUrlPickerItem, getUmbracoMediaUrl } from "utils"
import { BRUSH_UNDERLINE_URI } from "../brushUnderline"
import { handleAnchorScroll } from "../scrollToAnchor"
import { LANDING_ELEMENTS, useLandingAnalytics } from "../analytics"

type Props = {
  data: any
}

const MIN_TABS = 2
const MAX_TABS = 6

// --- Icons ---

/* The nav button is not a circle. Figma unions a 48px disc with a concave wedge
   so the shape necks out to the media edge (exported from 747:5052, the "Union"
   boolean). Raw path = disc on the left, wedge running right; mirrored for the
   button that sits on the opposite edge. */
const UNION_PATH =
  "M24 0C30.6436 0 36.6563 2.69971 41.002 7.06152L41 7C44.5 11 50.1567 15.3545 " +
  "59.6523 15.3545C69.1479 15.3544 77.1739 6.45159 80 2L80 43.5488C77.7391 " +
  "38.6026 70.5044 28.7101 59.6523 28.71C48.8002 28.71 45 36 42.5 39.5L42.4902 " +
  "39.2998C38.0881 44.614 31.4398 48 24 48C10.7452 48 0 37.2548 0 24C0 10.7452 " +
  "10.7452 0 24 0Z"

/* vuesax/linear/arrow-left — a slim chevron ~7 x 15.84 in a 24 box at stroke 1.5 */
const CHEVRON = {
  left: "M15 19.92 8.48 13.4a1.98 1.98 0 0 1 0-2.8L15 4.08",
  right: "M9 4.08 15.52 10.6a1.98 1.98 0 0 1 0 2.8L9 19.92",
}

/* `flip` mirrors the shape so the disc always faces inward and the wedge bleeds
   toward the nearest media edge. The chevron rides at the disc centre either way. */
function NavArrow({ dir, flip }: { dir: "left" | "right"; flip: boolean }) {
  return (
    <svg viewBox="0 0 80 48" fill="none" aria-hidden focusable="false">
      <path
        d={UNION_PATH}
        fill="#FFFFFF"
        fillOpacity="0.8"
        transform={flip ? "translate(80,0) scale(-1,1)" : undefined}
      />
      <g transform={`translate(${flip ? 44 : 12},12)`}>
        <path
          d={CHEVRON[dir]}
          stroke="#060720"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  )
}

function PauseIcon() {
  return (
    <svg width="18" height="22" viewBox="0 0 20 25" fill="#000" aria-hidden focusable="false">
      <rect x="1" y="0" width="8" height="25" rx="1.5" />
      <rect x="11" y="0" width="8" height="25" rx="1.5" />
    </svg>
  )
}

function PlayIcon() {
  return (
    <svg width="18" height="22" viewBox="0 0 20 25" fill="#000" aria-hidden focusable="false">
      <path d="M2 1.5v22l18-11L2 1.5z" />
    </svg>
  )
}

// --- Animation ---

/* Short cross-fade used both when switching tabs and when paging a gallery
   (ASH-1864: "transition between tab contents uses a short fade"). */
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

// --- Styled Components ---

const Section = styled.section`
  padding: 48px 20px;
  background-color: var(--comp-bg);
  color: var(--comp-text);

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 64px 120px;
  }
`

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  text-align: center;
  margin-bottom: 40px;

  ${(props) => props.theme.breakpoints.up("md")} {
    max-width: 840px;
    gap: 32px;
    margin-bottom: 48px;
    margin-inline: auto;
  }
`

/* Rich-text title. Accent word carries the brush-underline SVG (ASH-1549). */
const Title = styled.h2`
  font-size: 40px;
  font-weight: 700;
  line-height: 40px;
  color: var(--comp-heading);
  margin: 0;

  p {
    margin: 0;
    display: inline;
  }

  strong {
    font-weight: 700;
    display: inline-block;
    margin: 0;
    position: relative;
    text-decoration: none;
    &::after {
      content: "";
      position: absolute;
      bottom: -4px;
      inset-inline: 0;
      height: 6px;
      z-index: 1;
      background-color: currentColor;
      -webkit-mask: url("${BRUSH_UNDERLINE_URI}") no-repeat center / 100% 100%;
      mask: url("${BRUSH_UNDERLINE_URI}") no-repeat center / 100% 100%;
    }
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 60px;
    font-weight: 500;
    line-height: 72px;
  }
`

const OpeningText = styled.p`
  font-size: 18px;
  font-weight: 700;
  line-height: 27px;
  color: var(--comp-text);
  margin: 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 20px;
    font-weight: 400;
    line-height: 30px;
  }
`

/* ---- Desktop tab bar ---- */

const TabsMenu = styled.div`
  display: none;

  ${(props) => props.theme.breakpoints.up("md")} {
    display: flex;
    border: 1px solid var(--comp-text);
    border-bottom: none;
  }
`

/* Figma: every item carries a separator on its reading-start edge, and an
   inactive item keeps its bottom rule. The active item drops the bottom rule
   so it reads as joined to the pane below. */
const TabButton = styled.button<{ active: boolean }>`
  flex: 1;
  min-width: 0;
  padding: 24px 32px;
  border: none;
  border-inline-start: 1px solid var(--comp-text);
  border-bottom: 1px solid ${({ active }) => (active ? "transparent" : "var(--comp-text)")};
  background: transparent;
  color: var(--comp-heading);
  font-size: 24px;
  font-weight: 700;
  line-height: 31px;
  text-align: center;
  cursor: pointer;

  &:first-of-type {
    border-inline-start: none;
  }

  &:focus-visible {
    outline: 2px dashed var(--comp-text);
    outline-offset: -4px;
  }
`

/* ---- Desktop pane ---- */

const Pane = styled.div<{ swap: boolean }>`
  display: none;

  ${(props) => props.theme.breakpoints.up("md")} {
    display: flex;
    flex-direction: ${({ swap }) => (swap ? "row-reverse" : "row")};
    align-items: center;
    /* Columns sit at their Figma widths and shrink only when they must, with
       space-between absorbing the slack. At the 1440 design width that yields
       exactly 500 + 80 + 524; narrower viewports shrink instead of overflowing
       (a fixed 80px gap forced horizontal overflow at every width below 1440). */
    justify-content: space-between;
    gap: 40px;
    padding: 48px;
    border: 1px solid var(--comp-text);
    border-top: none;
    animation: ${fadeIn} 0.3s ease;
  }
`

const ContentCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  text-align: start;
  min-width: 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex: 0 1 500px;
    min-width: 0;
    max-width: 500px;
  }
`

const ContentText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const TabHeading = styled.h3`
  font-size: 28px;
  font-weight: 700;
  line-height: 40px;
  color: var(--comp-heading);
  margin: 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 40px;
    line-height: 40px;
  }
`

const TabText = styled.p`
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: var(--comp-text);
  margin: 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 18px;
    line-height: 26px;
  }
`

const Cta = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  /* Figma fixes the button box rather than letting it hug its label: 166x36
     mobile, 300x48 desktop. max-width keeps it inside a narrowed content
     column, and the explicit height stops the 29px line-height from pushing
     the desktop button to 53. */
  width: 166px;
  height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  background-color: var(--inner-bg);
  color: var(--inner-text);
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  text-decoration: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    width: 300px;
    height: 48px;
    font-size: 20px;
    line-height: 29px;
  }
`

/* ---- Media ---- */

const MediaBox = styled.div`
  position: relative;
  width: 100%;
  height: 272px;
  border-radius: 8px;
  overflow: hidden;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex: 0 1 524px;
    min-width: 0;
    max-width: 524px;
    height: auto;
    aspect-ratio: 524 / 536;
  }
`

/* The fade lives on a wrapper div rather than on styled(Image): emotion does
   not filter unknown props for custom components, so a `visible` prop set on
   next/image would land on the DOM <img> and trip a React warning. */
const Slide = styled.div<{ visible: boolean }>`
  position: absolute;
  inset: 0;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: opacity 0.4s ease;
`

const SlideImage = styled(Image)`
  object-fit: cover;
`

const MediaVideo = styled.video`
  position: absolute;
  inset: 0;
  height: 100%;
  object-fit: cover;
`

/* Flush to a media edge and vertically centred (Figma puts the row at y=244 of
   536). Physical left/right are set inline so the emotion RTL plugin cannot
   swap them — the pair is symmetric and must stay pinned to the same edges in
   both directions. */
const NavBtn = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 42px;
  height: 25px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  z-index: 2;

  svg {
    display: block;
    height: 100%;
  }

  &:hover svg path:first-of-type {
    fill-opacity: 0.95;
  }

  &:focus-visible {
    outline: 2px dashed #fff;
    outline-offset: -2px;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    width: 80px;
    height: 48px;
  }
`

const PlayPauseBtn = styled.button`
  position: absolute;
  bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #fff;
  cursor: pointer;
  z-index: 2;

  &:hover {
    opacity: 0.9;
  }

  &:focus-visible {
    outline: 2px dashed #fff;
    outline-offset: 2px;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    width: 50px;
    height: 50px;
  }
`

/* Desktop only — the mobile pane has no paging row in Figma, just the arrows.
   Bars are 32x4 with a 16 gap, centred, 22px above the media's bottom edge. */
const Bars = styled.div`
  display: none;

  ${(props) => props.theme.breakpoints.up("md")} {
    position: absolute;
    inset-inline: 0;
    bottom: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    z-index: 2;
  }
`

const Bar = styled.button<{ active: boolean }>`
  width: 32px;
  height: 6px;
  padding: 0;
  border: none;
  background-color: ${({ active }) => (active ? "#FFFFFF" : "#AAAAAA")};
  cursor: pointer;

  &:focus-visible {
    outline: 2px dashed #fff;
    outline-offset: 2px;
  }
`

/* ---- Mobile accordion ---- */

const Accordion = styled.div`
  display: flex;
  flex-direction: column;

  ${(props) => props.theme.breakpoints.up("md")} {
    display: none;
  }
`

const AccordionItem = styled.div<{ $isOpen: boolean }>`
  border: 1px solid var(--comp-text);
  margin-top: -1px;

  &:first-child {
    margin-top: 0;
  }
`

const AccordionHeader = styled.button`
  display: block;
  padding: 20px 24px;
  border: none;
  background: transparent;
  color: var(--comp-heading);
  font-size: 20px;
  font-weight: 700;
  line-height: 30px;
  text-align: start;
  cursor: pointer;

  &:focus-visible {
    outline: 2px dashed var(--comp-text);
    outline-offset: -4px;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    margin-inline: auto;
  }
`

/* 0fr -> 1fr animates to the panel's natural height without measuring it. */
const AccordionPanel = styled.div<{ open: boolean }>`
  display: grid;
  grid-template-rows: ${({ open }) => (open ? "1fr" : "0fr")};
  transition: grid-template-rows 0.35s ease;
`

const AccordionPanelInner = styled.div`
  overflow: hidden;
`

const MobilePaneBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  padding: 24px;
`

// --- Types & parsing ---

type MediaItem = { url: string; altText: string }

type Tab = {
  label: string
  title: string
  text: string
  media: MediaItem[]
  ctaHref?: string
  ctaLabel?: string
  ctaTarget?: string
}

function isVideoUrl(url: string): boolean {
  return /\.(mp4|webm)$/i.test(url)
}

function isToggleOn(raw: any): boolean {
  const v = Array.isArray(raw) ? raw[0] : raw
  return String(v).toLowerCase() === "true" || String(v) === "1"
}

/* The CMS aliases below follow the ASH-1862 field contract. Fallbacks cover the
   shorter names Umbraco editors sometimes end up with on the element type. */
function pick(block: any, aliases: string[], allMedia = false): any {
  for (const alias of aliases) {
    const value = extractProperty(block, alias, allMedia)
    if (value !== undefined && value !== null && value !== "") return value
  }
  return undefined
}

function parseTab(block: any): Tab {
  const rawMedia = (pick(block, ["media", "image", "images"], true) as any[]) || []
  const ctaLinks = pick(block, ["ctaButton", "button", "cta"]) as any[] | undefined
  const cta = Array.isArray(ctaLinks) ? ctaLinks[0] : undefined

  return {
    label: (pick(block, ["tabLabel", "label"]) as string) || "",
    title: (pick(block, ["tabContentTitle", "tabTitle", "title", "contentTitle"]) as string) || "",
    text: (pick(block, ["tabBodyText", "tabText", "text", "bodyText"]) as string) || "",
    media: (Array.isArray(rawMedia) ? rawMedia : [])
      .filter((m) => m?.url)
      .map((m) => ({ url: m.url as string, altText: (m.altText as string) || "" })),
    ctaHref: cta?.url ? getLinkHrefFromMultiUrlPickerItem(cta) : undefined,
    ctaLabel: cta?.name || undefined,
    ctaTarget: cta?.target || undefined,
  }
}

function isEmptyTab(tab: Tab): boolean {
  return !tab.label && !tab.title && !tab.text && tab.media.length === 0
}

// --- Media ---

/* Video slide inside a mixed gallery — plays programmatically when active,
   with a play/pause overlay button (same pattern as Gallery6). */
function GalleryVideoSlide({
  url,
  isActive,
  isRtl,
}: {
  url: string
  isActive: boolean
  isRtl: boolean
}) {
  const analytics = useLandingAnalytics()
  const ref = useRef<HTMLVideoElement>(null)
  const [paused, setPaused] = useState(true)

  useEffect(() => {
    if (ref.current && isActive) {
      ref.current.currentTime = 0
      ref.current.play()
      setPaused(false)
    } else if (ref.current && !isActive) {
      ref.current.pause()
      setPaused(true)
    }
  }, [isActive])

  return (
    <>
      <MediaVideo
        ref={ref}
        src={getUmbracoMediaUrl(url)}
        muted
        loop
        playsInline
        onPlay={() => setPaused(false)}
        onPause={() => setPaused(true)}
      />
      <PlayPauseBtn
        style={{ left: 16 }}
        onClick={() => {
          if (ref.current) {
            analytics.mediaClick(paused ? "play" : "pause", LANDING_ELEMENTS.aboutOpening)
            if (paused) ref.current.play()
            else ref.current.pause()
          }
        }}
        aria-label={
          paused ? (isRtl ? "הפעל וידאו" : "Play video") : isRtl ? "עצור וידאו" : "Pause video"
        }
      >
        {paused ? <PlayIcon /> : <PauseIcon />}
      </PlayPauseBtn>
    </>
  )
}

/* `active` is false for collapsed mobile accordion panels: their media stays in
   the DOM so the open/close height transition works, but it must not autoplay a
   video or claim LCP priority for a panel nobody is looking at. */
function TabMedia({
  media,
  isRtl,
  active,
}: {
  media: MediaItem[]
  isRtl: boolean
  active: boolean
}) {
  const analytics = useLandingAnalytics()
  const [index, setIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  // Play/pause single video based on panel visibility
  useEffect(() => {
    if (videoRef.current && active) {
      videoRef.current.play()
      setIsPlaying(true)
    } else if (videoRef.current) {
      videoRef.current.pause()
      setIsPlaying(false)
    }
  }, [active])

  if (media.length === 0) return null

  const videoItem = media.find((m) => isVideoUrl(m.url))
  const isVideo = media.length === 1 && videoItem !== undefined
  const hasGallery = !isVideo && media.length > 1

  const togglePlay = () => {
    const v = videoRef.current
    if (!v) return
    if (isPlaying) v.pause()
    else v.play()
    analytics.mediaClick(isPlaying ? "pause" : "play", LANDING_ELEMENTS.aboutOpening)
    setIsPlaying(!isPlaying)
  }

  const step = (delta: number) => setIndex((i) => (i + delta + media.length) % media.length)

  return (
    <MediaBox>
      {isVideo ? (
        <MediaVideo
          key={String(active)}
          ref={videoRef}
          src={getUmbracoMediaUrl(videoItem!.url)}
          preload={active ? "auto" : "none"}
          muted
          loop
          playsInline
        />
      ) : (
        media.map((item, i) => (
          <Slide key={item.url + i} visible={i === index}>
            {isVideoUrl(item.url) ? (
              <GalleryVideoSlide url={item.url} isActive={active && i === index} isRtl={isRtl} />
            ) : (
              <SlideImage
                src={getUmbracoMediaUrl(item.url)}
                alt={item.altText || ""}
                fill
                sizes="(max-width: 768px) 287px, 524px"
                priority={active && i === 0}
              />
            )}
          </Slide>
        ))
      )}

      {isVideo && (
        <PlayPauseBtn
          style={{ left: 16 }}
          onClick={togglePlay}
          aria-label={
            isPlaying
              ? isRtl
                ? "עצור תנועת הרכיב"
                : "Stop animation"
              : isRtl
              ? "הפעל תנועה רכיב"
              : "Play animation"
          }
        >
          {isPlaying ? <PauseIcon /> : <PlayIcon />}
        </PlayPauseBtn>
      )}

      {/* Arrows and bars appear only for a true gallery (ASH-1864 AC).
          The chevron always points at its own edge; which direction that steps
          depends on reading order, so in RTL the left button advances. */}
      {hasGallery && (
        <>
          <NavBtn
            style={{ left: 0 }}
            onClick={() => step(isRtl ? 1 : -1)}
            aria-label={isRtl ? "הבא" : "Previous"}
          >
            <NavArrow dir="left" flip />
          </NavBtn>
          <NavBtn
            style={{ right: 0 }}
            onClick={() => step(isRtl ? -1 : 1)}
            aria-label={isRtl ? "הקודם" : "Next"}
          >
            <NavArrow dir="right" flip={false} />
          </NavBtn>
          <Bars>
            {media.map((_, i) => (
              <Bar
                key={i}
                active={i === index}
                onClick={() => setIndex(i)}
                aria-label={isRtl ? `תמונה ${i + 1}` : `Image ${i + 1}`}
                aria-current={i === index}
              />
            ))}
          </Bars>
        </>
      )}
    </MediaBox>
  )
}

/* Desktop pane and mobile accordion are both always in the DOM, so the active
   tab's title was emitted as a heading twice - N tabs produced N+1 headings
   (ASH-2189). The accordion already yields exactly one per tab, so the pane
   renders its copy as plain text. Its tabpanel is still named by its tab via
   aria-labelledby, so nothing loses an accessible name. */
function TabBody({
  tab,
  isRtl,
  active,
  titleAsHeading = true,
}: {
  tab: Tab
  isRtl: boolean
  active: boolean
  titleAsHeading?: boolean
}) {
  const analytics = useLandingAnalytics()
  return (
    <>
      <ContentCol>
        <ContentText>
          {tab.title && <TabHeading as={titleAsHeading ? "h3" : "p"}>{tab.title}</TabHeading>}
          {tab.text && <TabText>{tab.text}</TabText>}
        </ContentText>
        {tab.ctaHref && tab.ctaLabel && (
          <Cta
            href={tab.ctaHref}
            onClick={(e) => {
              analytics.ctaClick(tab.ctaLabel || "", LANDING_ELEMENTS.aboutOpening)
              handleAnchorScroll(e, tab.ctaHref)
            }}
            {...(tab.ctaTarget === "_blank"
              ? { target: "_blank", rel: "noopener noreferrer" }
              : {})}
          >
            {tab.ctaLabel}
          </Cta>
        )}
      </ContentCol>
      <TabMedia media={tab.media} isRtl={isRtl} active={active} />
    </>
  )
}

// --- Main Component ---

function AboutOpening5({ data }: Props) {
  const locale = useLocale()
  const isRtl = locale === "he" || !locale
  const [activeIndex, setActiveIndex] = useState(0)
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  const title = (pick(data, ["title", "mainTitle"]) as string) || ""
  const openingText = (pick(data, ["openingText", "introText", "text"]) as string) || ""
  const swap = isToggleOn(pick(data, ["swapPosition", "swap"]))
  const tabBlocks = pick(data, ["tabs", "items"]) as any[] | undefined

  const tabs = (Array.isArray(tabBlocks) ? tabBlocks : [])
    .map(parseTab)
    .filter((tab) => !isEmptyTab(tab))
    .slice(0, MAX_TABS)

  if (!title && !openingText && tabs.length === 0) return null

  /* Umbraco enforces the 2-6 range, but a half-filled block list must not
     render a tab strip with a single tab. */
  const showTabs = tabs.length >= MIN_TABS
  const active = tabs[Math.min(activeIndex, tabs.length - 1)]

  return (
    <Section>
      <Inner>
        <Header>
          {title && <Title dangerouslySetInnerHTML={{ __html: title }} />}
          {openingText && <OpeningText>{openingText}</OpeningText>}
        </Header>

        {tabs.length > 0 && (
          <>
            {/* Desktop: one tab strip plus a single pane for the active tab. */}
            {showTabs && (
              <TabsMenu role="tablist" aria-label={isRtl ? "קטגוריות" : "Categories"}>
                {tabs.map((tab, i) => (
                  <TabButton
                    key={i}
                    role="tab"
                    id={`ao5-tab-${i}`}
                    active={i === activeIndex}
                    aria-selected={i === activeIndex}
                    aria-controls={`ao5-panel-${i}`}
                    tabIndex={i === activeIndex ? 0 : -1}
                    onClick={() => setActiveIndex(i)}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      const last = tabs.length - 1
                      let next = -1
                      if (e.key === "ArrowRight")
                        next = isRtl ? (i === 0 ? last : i - 1) : i === last ? 0 : i + 1
                      if (e.key === "ArrowLeft")
                        next = isRtl ? (i === last ? 0 : i + 1) : i === 0 ? last : i - 1
                      if (e.key === "Home") next = 0
                      if (e.key === "End") next = last
                      if (next >= 0) {
                        e.preventDefault()
                        setActiveIndex(next)
                        document.getElementById(`ao5-tab-${next}`)?.focus()
                      }
                    }}
                  >
                    {tab.label}
                  </TabButton>
                ))}
              </TabsMenu>
            )}

            {active && (
              /* key on the index so the fade replays on every tab switch */
              <Pane
                key={activeIndex}
                swap={swap}
                role="tabpanel"
                id={`ao5-panel-${activeIndex}`}
                aria-labelledby={`ao5-tab-${activeIndex}`}
                tabIndex={0}
              >
                <TabBody tab={active} isRtl={isRtl} active titleAsHeading={false} />
              </Pane>
            )}

            {/* Mobile: same tabs array, resolved by index — never by label,
                since two tabs may legitimately share one (ASH-1864 AC). */}
            <Accordion>
              {tabs.map((tab, i) => (
                <AccordionItem key={i} $isOpen={openIndex === i}>
                  <AccordionHeader
                    aria-expanded={openIndex === i}
                    onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  >
                    {tab.label}
                  </AccordionHeader>
                  <AccordionPanel open={openIndex === i}>
                    <AccordionPanelInner>
                      <MobilePaneBody>
                        <TabBody tab={tab} isRtl={isRtl} active={openIndex === i} />
                      </MobilePaneBody>
                    </AccordionPanelInner>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </>
        )}
      </Inner>
    </Section>
  )
}

export default AboutOpening5
